// Shared play-state types and pure helpers. The game-specific state itself lives in the
// per-game Zustand stores (see createPlayStore in the web app); this module holds the
// framework-agnostic types (GameConfig / GamePlayState / GamePlayActions) and the move-diff,
// undo, and board-initialisation helpers those stores build on.

import {
  Board,
  BoardMessage,
  BoardMessageType,
  CellChange,
  DomainUser as User,
  GameType,
  HintBoxState,
  isCellChange,
  LoadedPlay,
  MoveAction,
  PlayCompletion,
  PlayData,
  SimpleDate,
} from '../types';

export interface GamePlayState<BoardT, CellT> {
  user: User;
  board: BoardT;
  boardDate: SimpleDate;
  complete: boolean;
  playDuration: number;
  playData: PlayData<CellT>;
  winsHistory: PlayCompletion[];
  moveHistory: MoveAction[];
  /** DB row id of the play the client is currently working on. null means the
   * next save should INSERT a new row. Set to the returned id after any INSERT
   * so subsequent saves target that specific row. Cleared to null on reset. */
  currentPlayId: number | null;
  /** True only when the board was completed by a move in THIS session. Opening an already-finished
   *  puzzle — from the archive, or on a reload — shows the solved screen with this false, so anything
   *  that only makes sense about a fresh solve can tell the two apart. `completedAt` cannot: it is
   *  populated from the stored play's created_at when an existing completion is loaded. */
  justCompleted: boolean;
}

/** Given the play data before and after a cell change, the play data the whole move should leave. */
export type DeriveMove<CellT> = (previous: PlayData<CellT>, next: PlayData<CellT>) => PlayData<CellT>;

export interface GamePlayActions<BoardT, CellT> {
  /**
   * `derive`, when given, adds whatever else the player's change implies to the same move, so one
   * Undo takes back both: Mosaic's auto-complete ticks off the clues of the lines a move finished.
   */
  setCell: (row: number, col: number, cell: CellT, derive?: DeriveMove<CellT>) => void;
  setCells: (cells: CellT[][], derive?: DeriveMove<CellT>) => void;
  /**
   * Make a move that isn't a cell the player set — a game's own annotations (Mosaic's ticked-off
   * clue numbers), and any cells they change along with them. It goes into moveHistory like any
   * other move, but it isn't reported to onMove.
   */
  applyMove: (update: (playData: PlayData<CellT>) => PlayData<CellT>) => void;
  handleUndo: () => void;
  /** Clears the board. `undoable` keeps the move history and adds the reset to it, so Undo restores the board. */
  handleReset: (options?: { undoable?: boolean }) => void;
  fillBoardToOneMoveAway: () => void;
  /** Call with the id returned by savePlay after a successful INSERT so that
   * all subsequent saves for this session target that specific row. */
  setCurrentPlayId: (id: number) => void;
}

/**
 * The check/hint feedback surface — the transient message channel (errors, hints, tutorial text)
 * and the two-click Check / Hint controls. Deliberately game-agnostic: it deals only in core
 * BoardMessage / HintBoxState, so components can consume it without knowing the game. The
 * auto-check reminder and the game-specific check/hint logic live behind these actions.
 */
export interface CheckHintState {
  boardMessages: BoardMessage[];
  hintBoxState: HintBoxState;
}

export interface CheckHintActions {
  handleCheckBoard: () => void;
  handleHintRequested: () => void;
  /**
   * Whether pressing Hint now would tell the player something: the board has errors to show, or a
   * strategy finds a hint. False only for an error-free board with no hint to give.
   */
  isHintAvailable: () => boolean;
  setHintBoxState: (state: HintBoxState) => void;
  setBoardMessages: (messages: BoardMessage[]) => void;
}

export type CheckHintContext = CheckHintState & CheckHintActions;

// Define a generic PlayStateContext for generic components that need parts of the PlayStateContext
// but don't care what game they're talking about. We have to use any for CellT because
// GamePlayState<BoardT, CellT> has CellT in both covariant positions (playData: PlayData<CellT>)
// and contravariant positions (setCell: (cell: CellT) => void).

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GenericPlayStateContext = GamePlayState<Board, any> & GamePlayActions<Board, any>;

export interface GameConfig<BoardT, CellT> {
  gameType: GameType;
  initialPlayData: PlayData<CellT>;
  clearBoardMessages?: boolean;
  skipCheckConfirmation?: boolean;
  getInitialCells: (board: BoardT) => CellT[][];
  getCompleteBoardCells: (board: BoardT) => CellT[][];
  canBoardBeComplete: (board: BoardT, playState: GamePlayState<BoardT, CellT>) => boolean;
  createAlmostCompleteState: (board: BoardT) => CellT[][];
  shouldAutoCheck: (playState: GamePlayState<BoardT, CellT>) => boolean;
  checkBoard: (cells: CellT[][], board: BoardT) => BoardMessage[];
  /** The next hint, or null when no strategy finds one; `noHintsMessage` is shown then. */
  generateHint?: (cells: CellT[][], board: BoardT) => BoardMessage | null;
  noHintsMessage?: BoardMessage;
  /** Fired when the player begins an attempt: on load of a fresh or in-progress puzzle, and again
   * when a replay starts after a completion (via reset). `resumed` is true when picking up saved
   * in-progress play on load; `replay` is how many times this puzzle was completed before this
   * attempt. Not fired when opening an already-completed board — no attempt begins until Play Again. */
  onStart: (board: BoardT, boardDate: SimpleDate | undefined, user: User, resumed: boolean, replay: number) => void;
  onMove?: (board: BoardT, boardDate: SimpleDate | undefined, move?: { row: number; col: number; cell: CellT }) => void;
  onCompletion: (
    board: BoardT,
    boardDate: SimpleDate | undefined,
    user: User,
    playData: PlayData<CellT>,
    playDuration: number
  ) => void;
}

export const firstCheckClickMessage: BoardMessage = {
  message: 'You have at least one error on your board. Click Check again to reveal them.',
  markedCells: [],
  type: BoardMessageType.error,
};

/** Shown when the game's own `noHintsMessage` is missing. */
export const defaultNoHintsMessage: BoardMessage = {
  message: 'No hints available.',
  markedCells: [],
  type: BoardMessageType.hint,
};

/** Shown for a Check on a board that's still as it started; such a Check isn't counted. */
export const nothingToCheckMessage: BoardMessage = {
  message: "There's nothing to check.",
  markedCells: [],
  type: BoardMessageType.hint,
};

export const noErrorsMessage: BoardMessage = {
  message: 'Congrats, no errors here!',
  markedCells: [],
  type: BoardMessageType.success,
};

export const hasSameArrayShape = (a: unknown, b: unknown): boolean => {
  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);

  if (aIsArray !== bIsArray) {
    return false;
  }

  if (!aIsArray && !bIsArray) {
    return true;
  }

  const arrA = a as unknown[];
  const arrB = b as unknown[];

  if (arrA.length !== arrB.length) {
    return false;
  }

  return arrA.every((item, index) => hasSameArrayShape(item, arrB[index]));
};

/**
 * Play-data fields a move never records: the cells have their own per-cell changes, and the rest is
 * bookkeeping about the play rather than the player's work on it, which Undo must not wind back.
 * `moveHistory` is where legacy plays kept their history inside the blob.
 */
const NOT_MOVES = new Set(['cells', 'playStats', 'savedAt', 'moveHistory']);

export function isValidMoveAction(moveAction: unknown): moveAction is MoveAction {
  if (!Array.isArray(moveAction)) {
    return false;
  }

  return moveAction.every((change) => {
    if (!Array.isArray(change)) {
      return false;
    }
    if (change.length === 2) {
      return typeof change[0] === 'string' && !NOT_MOVES.has(change[0]);
    }
    if (change.length !== 3) {
      return false;
    }
    const row = change[0];
    const col = change[1];
    return Number.isInteger(row) && Number.isInteger(col) && row >= 0 && col >= 0;
  });
}

function normalizeMoveHistory(moveHistory: unknown): MoveAction[] {
  if (!Array.isArray(moveHistory) || !moveHistory.every(isValidMoveAction)) {
    return [];
  }
  return moveHistory as MoveAction[];
}

/** The move that takes `previous` to `next`: its changed cells, then its changed fields. */
export function computeMove<CellT>(previous: PlayData<CellT>, next: PlayData<CellT>): MoveAction {
  const move = computeDiffFromCells(previous.cells, next.cells);
  const prevFields = previous as unknown as Record<string, unknown>;
  const nextFields = next as unknown as Record<string, unknown>;
  for (const key of new Set([...Object.keys(prevFields), ...Object.keys(nextFields)])) {
    if (!NOT_MOVES.has(key) && !Object.is(prevFields[key], nextFields[key])) move.push([key, prevFields[key] ?? null]);
  }
  return move;
}

/** Takes `move` back off `playData`. */
export function applyReverseMove<CellT>(playData: PlayData<CellT>, move: MoveAction): PlayData<CellT> {
  const cellChanges = move.filter(isCellChange);
  const reverted: Record<string, unknown> = {
    ...playData,
    cells: cellChanges.length ? applyReverseMoveToCells(playData.cells, cellChanges) : playData.cells,
  };
  for (const change of move) {
    if (isCellChange(change)) continue;
    const [key, prevValue] = change;
    if (prevValue === null || prevValue === undefined) delete reverted[key];
    else reverted[key] = prevValue;
  }
  return reverted as unknown as PlayData<CellT>;
}

function computeDiffFromCells(previousCells: unknown, nextCells: unknown): MoveAction {
  if (!Array.isArray(previousCells) || !Array.isArray(nextCells)) {
    return [];
  }

  const moveAction: MoveAction = [];
  const oldRows = previousCells as unknown[][];
  const newRows = nextCells as unknown[][];
  const maxRows = Math.max(oldRows.length, newRows.length);

  for (let row = 0; row < maxRows; row++) {
    const oldRow = Array.isArray(oldRows[row]) ? oldRows[row] : [];
    const newRow = Array.isArray(newRows[row]) ? newRows[row] : [];
    const maxCols = Math.max(oldRow.length, newRow.length);

    for (let col = 0; col < maxCols; col++) {
      const prevValue = oldRow[col];
      const nextValue = newRow[col];
      if (!Object.is(prevValue, nextValue)) {
        moveAction.push([row, col, prevValue]);
      }
    }
  }

  return moveAction;
}

function applyReverseMoveToCells(cells: unknown, moveAction: CellChange[]): unknown {
  if (!Array.isArray(cells)) {
    return cells;
  }

  const nextCells = (cells as unknown[][]).map((row) => (Array.isArray(row) ? [...row] : row));
  for (const [row, col, prevValue] of moveAction) {
    if (Array.isArray(nextCells[row])) {
      nextCells[row][col] = prevValue;
    }
  }

  return nextCells;
}

/**
 * Whether an unfinished play's stored cells can be drawn on this board. A play saved with cells of
 * another shape can't be, and a board handed them reads past its own edge; such a play opens blank.
 */
function storedCellsFit<BoardT extends Board, CellT>(
  board: BoardT,
  config: GameConfig<BoardT, CellT>,
  play: LoadedPlay
): boolean {
  const cells = play.play_data.cells;
  return cells != null && hasSameArrayShape(cells, config.getInitialCells(board));
}

function buildInitialPlayData<BoardT extends Board, CellT>(
  board: BoardT,
  config: GameConfig<BoardT, CellT>,
  initialPlay: LoadedPlay | null
): PlayData<CellT> {
  const initialCells = config.getInitialCells(board);
  if (!initialPlay) {
    return {
      ...config.initialPlayData,
      cells: initialCells,
      playStats: { hintsUsed: 0, checksUsed: 0, pauseCount: 0 },
    };
  }
  const existingPlayStats = initialPlay.play_data.playStats;
  const cells = initialPlay.complete
    ? config.getCompleteBoardCells(board)
    : storedCellsFit(board, config, initialPlay)
      ? initialPlay.play_data.cells
      : initialCells;
  return {
    ...initialPlay.play_data,
    cells,
    playStats: {
      hintsUsed: existingPlayStats?.hintsUsed ?? 0,
      checksUsed: existingPlayStats?.checksUsed ?? 0,
      pauseCount: existingPlayStats?.pauseCount ?? 0,
    },
  } as PlayData<CellT>;
}

// The play-related state derived from a loaded board + play, bundled so a store can adopt
// it atomically when the board changes (board.id, currentPlayId, moveHistory, etc. all move
// together — never a mix of old and new). Built by buildBoardPlayState below.
interface BoardPlayState<CellT> {
  boardId: number;
  complete: boolean;
  playDuration: number;
  playData: PlayData<CellT>;
  winsHistory: PlayCompletion[];
  moveHistory: MoveAction[];
  currentPlayId: number | null;
  completedAt: Date | null;
  /** True only when the board was completed by a move in THIS session. Opening an already-finished
   *  puzzle — from the archive, or on a reload — shows the solved screen with this false, so anything
   *  that only makes sense about a fresh solve can tell the two apart. `completedAt` cannot: it is
   *  populated from the stored play's created_at when an existing completion is loaded. */
  justCompleted: boolean;
}

export function buildBoardPlayState<BoardT extends Board, CellT>(
  board: BoardT,
  config: GameConfig<BoardT, CellT>,
  initialPlay: LoadedPlay | null
): BoardPlayState<CellT> {
  return {
    boardId: board.id,
    complete: initialPlay?.complete ?? false,
    playDuration: initialPlay?.play_duration ?? 0,
    playData: buildInitialPlayData(board, config, initialPlay),
    winsHistory: initialPlay?.winsHistory ?? [],
    // Moves recorded against cells the board didn't keep would undo into the wrong shape.
    moveHistory:
      initialPlay && (initialPlay.complete || storedCellsFit(board, config, initialPlay))
        ? normalizeMoveHistory(initialPlay.moveHistory ?? [])
        : [],
    currentPlayId: initialPlay?.id ?? null,
    completedAt: initialPlay?.complete && initialPlay.created_at ? new Date(initialPlay.created_at) : null,
    // Loading a finished play is not completing it.
    justCompleted: false,
  };
}
